/**
 * Frontend metadata for delivery-platform providers (label, brand color,
 * OAuth capability, feature capability flags). Keys are the lowercase
 * `source` token written to OrderTicket.source on the backend. Adding a
 * provider here requires a matching backend ProviderRegistry::register()
 * call.
 *
 * Capability flags drive conditional UI rendering: the POS shows the
 * adjust-prep button only when capabilities.adjustPreparation is true,
 * etc. Wolt's capability matrix is all-false except for OAuth — its
 * provider class no-ops the methods. Glovo (DH) supports all four.
 */
const REGISTRY = {
    glovo: {
        source: 'glovo',
        label: 'Glovo',
        brandColor: '#daa520',
        accentColor: 'rgba(255, 194, 68, 0.15)',
        // Glovo runs on Delivery Hero's middleware API — OAuth
        // client_credentials, not authorization_code. There's no
        // user-redirect "Connect with Glovo" flow; the admin pastes
        // credentials (chain code / client id / client secret /
        // webhook signing secret) into the admin form. This flag is
        // specifically "uses the authorization-code redirect flow",
        // so it stays false even though DH's outbound auth is OAuth.
        oauth: false,
        capabilities: {
            adjustPreparation: true,
            structuredRejection: true,
            setStoreStatus: true,
            rejectionReasonsApi: '/api/delivery/glovo/rejection-reasons',
        },
    },
    wolt: {
        source: 'wolt',
        label: 'Wolt',
        brandColor: '#38b6ff',
        accentColor: 'rgba(0, 157, 224, 0.15)',
        oauth: true,
        capabilities: {
            adjustPreparation: false,
            structuredRejection: false,
            setStoreStatus: false,
            rejectionReasonsApi: null,
        },
    },
};
export function getProviderMeta(source) {
    if (!source)
        return null;
    return REGISTRY[source.toLowerCase()] ?? null;
}
export function getProviderLabel(source) {
    return getProviderMeta(source)?.label ?? source ?? '';
}
/**
 * Providers that use authorization-code OAuth (the merchant clicks
 * "Connect with X" and is redirected). The `oauth` flag means exactly
 * this — providers using client_credentials OAuth (Glovo on DH) onboard
 * via the admin form instead and stay false here.
 */
export function listOAuthProviders() {
    return Object.values(REGISTRY).filter(p => p.oauth);
}
/**
 * Shortcut for conditional UI rendering.
 *   v-if="hasCapability(ticket.source, 'adjustPreparation')"
 */
export function hasCapability(source, capability) {
    const meta = getProviderMeta(source);
    if (!meta)
        return false;
    return Boolean(meta.capabilities[capability]);
}
//# sourceMappingURL=DeliveryProviderRegistry.js.map